// @tennis-matches/clubmeisterschaft-core/ranking – Group ranking v1 (pure function)

import { __asStr } from "@tennis-matches/core/utils";

/**
 * Build per-group rankings from schedule + results.
 * @param {object} input
 * @param {object} input.schedule built schedule
 * @param {object} input.results results map/list (see normalizeResults)
 * @param {object} input.points { win:number, draw:number }
 * @param {Array<{pid:string,name:string}>} input.players
 * @returns {object} map groupId -> { groupId, rows: sorted rows }
 */
export function buildGroupRankings({ schedule, results, points, players }) {
  const ptsWin = Number(points?.win ?? 2);
  const ptsDraw = Number(points?.draw ?? 1);

  const nameById = new Map((players || []).map((p) => [p.pid, __asStr(p.name)]));
  const resByMatchId = normalizeResults(results);

  const out = {};
  for (const g of schedule?.groups || []) {
    const groupId = g.id;
    const rows = new Map();
    for (const pid of g.playerIds || []) {
      rows.set(pid, makeRow(pid, nameById.get(pid)));
    }

    const matches = (schedule?.groupMatches || []).filter((m) => m.groupId === groupId);
    for (const m of matches) {
      const r = resByMatchId.get(m.id);
      if (!r || r.status !== "confirmed") continue;

      const a = rows.get(m.aId) || makeRow(m.aId, nameById.get(m.aId));
      const b = rows.get(m.bId) || makeRow(m.bId, nameById.get(m.bId));
      rows.set(m.aId, a);
      rows.set(m.bId, b);

      applyResult({ a, b, r, ptsWin, ptsDraw });
    }

    const sorted = Array.from(rows.values()).sort((x, y) => {
      if (y.points !== x.points) return y.points - x.points;
      const sdX = x.setsWon - x.setsLost;
      const sdY = y.setsWon - y.setsLost;
      if (sdY !== sdX) return sdY - sdX;
      const gdX = x.gamesWon - x.gamesLost;
      const gdY = y.gamesWon - y.gamesLost;
      if (gdY !== gdX) return gdY - gdX;
      if (y.gamesWon !== x.gamesWon) return y.gamesWon - x.gamesWon;
      return __asStr(x.name).localeCompare(__asStr(y.name));
    });

    out[groupId] = { groupId, rows: sorted };
  }

  return out;
}

function makeRow(pid, name) {
  return {
    pid,
    name: __asStr(name ?? ""),
    played: 0,
    wins: 0,
    draws: 0,
    losses: 0,
    points: 0,
    setsWon: 0,
    setsLost: 0,
    gamesWon: 0,
    gamesLost: 0,
  };
}

/**
 * Normalize results into Map(matchId -> {status, setsA, setsB, gamesA, gamesB}).
 * Accepted shapes: Map, Array of { matchId|id, status, setsA, setsB, gamesA, gamesB }, Object keyed by matchId.
 */
function normalizeResults(results) {
  if (!results) return new Map();
  if (results instanceof Map) return results;

  const map = new Map();
  if (Array.isArray(results)) {
    for (const r of results) {
      const key = __asStr(r.matchId ?? r.id ?? "");
      if (key) map.set(key, r);
    }
    return map;
  }
  if (typeof results === "object") {
    for (const [k, v] of Object.entries(results)) map.set(__asStr(k), v);
  }
  return map;
}

function applyResult({ a, b, r, ptsWin, ptsDraw }) {
  const setsA = Number(r.setsA ?? 0);
  const setsB = Number(r.setsB ?? 0);
  const gamesA = Number(r.gamesA ?? 0);
  const gamesB = Number(r.gamesB ?? 0);

  a.played++;
  b.played++;
  a.setsWon += setsA;
  a.setsLost += setsB;
  b.setsWon += setsB;
  b.setsLost += setsA;
  a.gamesWon += gamesA;
  a.gamesLost += gamesB;
  b.gamesWon += gamesB;
  b.gamesLost += gamesA;

  if (setsA > setsB) {
    a.wins++;
    b.losses++;
    a.points += ptsWin;
  } else if (setsB > setsA) {
    b.wins++;
    a.losses++;
    b.points += ptsWin;
  } else {
    a.draws++;
    b.draws++;
    a.points += ptsDraw;
    b.points += ptsDraw;
  }
}
